import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, Check, Zap, CalendarDays } from "lucide-react";
import { format } from "date-fns";
import DateTimePicker from "../common/DateTimePicker";

const DURATION_OPTIONS = [
  { label: "1 Week", days: 7 },
  { label: "2 Weeks", days: 14 },
  { label: "1 Month", days: 30 },
  { label: "3 Months", days: 90 },
  { label: "6 Months", days: 180 },
  { label: "1 Year", days: 365 },
];

export const TimelinePicker = ({ onSelect }) => {
  const [mode, setMode] = useState("launch-now");
  const [startDate, setStartDate] = useState(undefined);
  const [endDate, setEndDate] = useState(undefined);
  const startPickerRef = useRef(null);
  const endPickerRef = useRef(null);

  const getDayStart = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = getDayStart(new Date());
  const isEndBeforeStart = Boolean(
    mode === "scheduled" &&
    startDate &&
    endDate &&
    getDayStart(endDate).getTime() < getDayStart(startDate).getTime(),
  );
  const isStartInPast = Boolean(
    mode === "scheduled" &&
    startDate &&
    getDayStart(startDate).getTime() < today.getTime(),
  );
  const isEndInPast = Boolean(
    endDate && getDayStart(endDate).getTime() < today.getTime(),
  );

  const handleQuickDuration = (days) => {
    const baseDate =
      mode === "scheduled" && startDate ? new Date(startDate) : new Date();
    const end = new Date(baseDate);
    end.setDate(end.getDate() + days);
    setEndDate(end);
    if (mode === "launch-now") {
      setStartDate(undefined);
    }
  };

  const handleConfirm = () => {
    if (!endDate || isEndBeforeStart || isStartInPast || isEndInPast) return;
    onSelect(mode === "scheduled" ? startDate || null : null, endDate);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="gm-timeline"
    >
      <div className="gm-timeline-modes">
        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={() => setMode("launch-now")}
          className={`gm-timeline-mode-btn ${mode === "launch-now" ? "gm-timeline-mode-btn--active" : ""}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          >
            <path
              d="M2.99999 10.5C2.85807 10.5005 2.71892 10.4607 2.59871 10.3852C2.4785 10.3098 2.38217 10.2018 2.32091 10.0737C2.25964 9.94572 2.23596 9.80294 2.25262 9.66199C2.26927 9.52105 2.32558 9.38772 2.41499 9.2775L9.83999 1.6275C9.89569 1.56321 9.97159 1.51977 10.0552 1.5043C10.1389 1.48883 10.2253 1.50226 10.3003 1.54238C10.3753 1.5825 10.4344 1.64692 10.468 1.72508C10.5016 1.80324 10.5076 1.89049 10.485 1.9725L9.04499 6.4875C9.00253 6.60114 8.98827 6.72339 9.00344 6.84376C9.0186 6.96412 9.06274 7.07901 9.13206 7.17857C9.20138 7.27813 9.29382 7.35939 9.40145 7.41537C9.50908 7.47135 9.62868 7.50039 9.74999 7.5H15C15.1419 7.49952 15.2811 7.53931 15.4013 7.61477C15.5215 7.69022 15.6178 7.79823 15.6791 7.92626C15.7403 8.05428 15.764 8.19706 15.7474 8.33801C15.7307 8.47895 15.6744 8.61228 15.585 8.7225L8.15999 16.3725C8.1043 16.4368 8.0284 16.4802 7.94476 16.4957C7.86112 16.5112 7.7747 16.4977 7.6997 16.4576C7.62469 16.4175 7.56556 16.3531 7.53199 16.2749C7.49843 16.1968 7.49244 16.1095 7.51499 16.0275L8.95499 11.5125C8.99746 11.3989 9.01172 11.2766 8.99655 11.1562C8.98139 11.0359 8.93725 10.921 8.86793 10.8214C8.7986 10.7219 8.70616 10.6406 8.59854 10.5846C8.49091 10.5286 8.37131 10.4996 8.24999 10.5H2.99999Z"
              stroke="#DAD9D9"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span className="gm-timeline-mode-label">Launch Now</span>
        </motion.button>

        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={() => setMode("scheduled")}
          className={`gm-timeline-mode-btn ${mode === "scheduled" ? "gm-timeline-mode-btn--active" : ""}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          >
            <path
              d="M6 1.5V4.5"
              stroke="#DAD9D9"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12 1.5V4.5"
              stroke="#DAD9D9"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M14.25 3H3.75C2.92157 3 2.25 3.67157 2.25 4.5V15C2.25 15.8284 2.92157 16.5 3.75 16.5H14.25C15.0784 16.5 15.75 15.8284 15.75 15V4.5C15.75 3.67157 15.0784 3 14.25 3Z"
              stroke="#DAD9D9"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M2.25 7.5H15.75"
              stroke="#DAD9D9"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span className="gm-timeline-mode-label">Schedule</span>
        </motion.button>
      </div>

      <AnimatePresence>
        {mode === "scheduled" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="gm-timeline-start"
          >
            <label className="gm-timeline-label">
              <Clock className="gm-icon-sm gm-text-primary" />
              Start Date
            </label>
            <div
              className="gm-date-trigger"
              onClick={() => startPickerRef.current?.open()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M6 1.50049V4.50049"
                  stroke="#DAD9D9"
                  stroke-width="1.22727"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 1.50049V4.50049"
                  stroke="#DAD9D9"
                  stroke-width="1.22727"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M14.25 2.99951H3.75C2.92157 2.99951 2.25 3.67108 2.25 4.49951V14.9995C2.25 15.8279 2.92157 16.4995 3.75 16.4995H14.25C15.0784 16.4995 15.75 15.8279 15.75 14.9995V4.49951C15.75 3.67108 15.0784 2.99951 14.25 2.99951Z"
                  stroke="#DAD9D9"
                  stroke-width="1.22727"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2.25 7.49951H15.75"
                  stroke="#DAD9D9"
                  stroke-width="1.22727"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span
                className={`gm-date-trigger-text ${startDate ? "gm-date-trigger-text--filled" : ""}`}
              >
                {startDate ? (
                  <>
                    {startDate
                      ? format(startDate, "dd/MM/yyyy hh:mm aa")
                      : "dd/MM/yyyy hh:mm"}
                  </>
                ) : (
                  "Pick a custom date"
                )}
              </span>
              <DateTimePicker
                ref={startPickerRef}
                value={startDate}
                onChange={(date) => {
                  if (!date) {
                    setStartDate(undefined);
                    return;
                  }
                  if (getDayStart(date).getTime() < today.getTime()) return;
                  setStartDate(date);
                  if (
                    endDate &&
                    getDayStart(endDate).getTime() < getDayStart(date).getTime()
                  ) {
                    setEndDate(undefined);
                  }
                }}
                minDateTime={new Date()}
                placeholder="dd/MM/yyyy hh:mm"
                format="dd/MM/yyyy hh:mm aa"
                oneTap={false}
                menuClassName="gm-picker-popup-dark positionsetpicker"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="gm-timeline-end-section">
        <label className="gm-timeline-label">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M6.66669 1.66748V5.00081"
              stroke="#E6FF00"
              stroke-width="1.36364"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M13.3333 1.66748V5.00081"
              stroke="#E6FF00"
              stroke-width="1.36364"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M15.8333 3.33252H4.16667C3.24619 3.33252 2.5 4.07871 2.5 4.99919V16.6659C2.5 17.5863 3.24619 18.3325 4.16667 18.3325H15.8333C16.7538 18.3325 17.5 17.5863 17.5 16.6659V4.99919C17.5 4.07871 16.7538 3.33252 15.8333 3.33252Z"
              stroke="#E6FF00"
              stroke-width="1.36364"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M2.5 8.33252H17.5"
              stroke="#E6FF00"
              stroke-width="1.36364"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {mode === "launch-now" ? "Resolution Date" : "End Date"}
        </label>

        <div className="gm-timeline-grid">
          {DURATION_OPTIONS.map((option) => {
            const baseDate =
              mode === "scheduled" && startDate
                ? new Date(startDate)
                : new Date();
            const optionDate = new Date(baseDate);
            optionDate.setDate(optionDate.getDate() + option.days);
            const isSelected =
              endDate &&
              format(endDate, "yyyy-MM-dd") ===
                format(optionDate, "yyyy-MM-dd");

            return (
              <motion.button
                key={option.label}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleQuickDuration(option.days)}
                className={`gm-timeline-duration-btn ${isSelected ? "gm-timeline-duration-btn--active" : ""}`}
              >
                {option.label}
              </motion.button>
            );
          })}
        </div>

        <div className="gm-timeline-divider">
          <div className="gm-timeline-divider-line" />
          <div className="gm-timeline-divider-text">
            <span>or pick custom</span>
          </div>
          <div className="gm-timeline-divider-line" />
        </div>

        <div
          className="gm-date-trigger"
          onClick={() => endPickerRef.current?.open()}
        >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
  <path d="M6 1.50049V4.50049" stroke="#DAD9D9" stroke-width="1.22727" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M12 1.50049V4.50049" stroke="#DAD9D9" stroke-width="1.22727" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M14.25 2.99951H3.75C2.92157 2.99951 2.25 3.67108 2.25 4.49951V14.9995C2.25 15.8279 2.92157 16.4995 3.75 16.4995H14.25C15.0784 16.4995 15.75 15.8279 15.75 14.9995V4.49951C15.75 3.67108 15.0784 2.99951 14.25 2.99951Z" stroke="#DAD9D9" stroke-width="1.22727" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M2.25 7.49951H15.75" stroke="#DAD9D9" stroke-width="1.22727" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
          <span
            className={`gm-date-trigger-text ${endDate ? "gm-date-trigger-text--filled" : ""}`}
          >

            {
              endDate ?
              <>
                {endDate
              ? format(endDate, "dd/MM/yyyy hh:mm aa")
              : "dd/MM/yyyy hh:mm"}
                      </>
              :
              'Pick a custom date'
            }
          
          </span>
          <DateTimePicker
            ref={endPickerRef}
            value={endDate}
            onChange={(date) => {
              if (!date) {
                setEndDate(undefined);
                return;
              }
              if (getDayStart(date).getTime() >= today.getTime()) {
                setEndDate(date);
              }
            }}
            minDateTime={
              mode === "scheduled" && startDate ? startDate : new Date()
            }
            placeholder="dd/MM/yyyy hh:mm"
            format="dd/MM/yyyy hh:mm aa"
            oneTap={false}
            menuClassName="gm-picker-popup-dark positionsetpicker"
          />
        </div>
      </div>

      {endDate && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="gm-timeline-summary"
        >
          <div className="gm-timeline-summary-inner">
            <motion.div
              className="gm-timeline-summary-icon"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Clock className="gm-icon-md gm-text-primary" />
            </motion.div>
            <div>
              {mode === "launch-now" ? (
                <p className="gm-text-foreground">
                  Launches{" "}
                  <span className="gm-text-bold gm-text-primary">
                    immediately
                  </span>
                  , resolves on{" "}
                  <span className="gm-text-bold">
                    {format(endDate, "PPPP")}
                  </span>
                </p>
              ) : (
                <p className="gm-text-foreground">
                  {startDate ? (
                    <>
                      Starts{" "}
                      <span className="gm-text-bold">
                        {format(startDate, "MMM d")}
                      </span>
                      , ends{" "}
                      <span className="gm-text-bold">
                        {format(endDate, "MMM d, yyyy")}
                      </span>
                    </>
                  ) : (
                    <>Select a start date</>
                  )}
                </p>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {isEndBeforeStart && (
        <p className="gm-timeline-error">
          End date must be the same day or later than launch date.
        </p>
      )}
      {isStartInPast && (
        <p className="gm-timeline-error">Start date cannot be in the past.</p>
      )}
      {isEndInPast && (
        <p className="gm-timeline-error">End date cannot be in the past.</p>
      )}

      <button
        onClick={handleConfirm}
        disabled={
          !endDate ||
          (mode === "scheduled" && !startDate) ||
          isEndBeforeStart ||
          isStartInPast ||
          isEndInPast
        }
        className="gm-primary-btn gm-primary-btn--tall"
      >
        Confirm Timeline
      </button>
    </motion.div>
  );
};
